import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { BranchType } from "../../../interfaces/delivery-company/branches";
import { ReactNode, useContext, useEffect, useState, useRef } from "react";
import { FaWarehouse } from "react-icons/fa";
import { OrgSettingsContext } from "../../../contexts/org-settings-context";
import axios from "axios";
import { API_ENDPOINT } from "../../../configurations/global.config";
import { useSnackBar } from "../../../contexts/snackbar";
import { AuthContext } from "../../../contexts/user-context/user-context";

// ------------------ Branch Selector ------------------
interface BranchSelectorProps {
    selectedBranch?: BranchType | null;
    selectedBranches?: BranchType[];
    onSelect?: (branch: BranchType) => void;
    onSelectMultiple?: (branches: BranchType[]) => void;
    selectFirstOnLoad?: boolean;
    defaultSelectedBranchID?: string | number | null;
    multiSelect?: boolean;
    footer?: (b: BranchType, isSelected: boolean) => ReactNode;
    filter?: (b: BranchType) => boolean
}

// ------------------ Branch Card ------------------
interface BranchCardProps {
    branch: BranchType;
    selected: boolean;
    onClick: (branch: BranchType) => void;
    width?: number;
    height?: number;
    footer?: (b: BranchType, isSelected: boolean) => ReactNode;
  }
  
 export const BranchCard = ({ 
          branch,
          selected,
          onClick,
          width=110,
          height= 110,
          footer = (b: BranchType,
          selected: boolean) => null,
        }: BranchCardProps) => {
    const { color } = useContext(OrgSettingsContext);
  
    return (
      <Box
        onClick={() => onClick(branch)}
        sx={{
          flex: "0 0 auto",
          width: width,
          height: height,
          mx: 1.2,
          borderRadius: 3,
          cursor: "pointer",
          border: selected ? `1.5px solid ${color}` : undefined,
          color: "#444",
          background: selected ? `${color}30` : undefined,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: selected ? 5 : 1,
          transition: "all 0.25s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: 4,
          },
        }}
      >
        <Tooltip title={branch.name}>
          <FaWarehouse size={24} color={color} />
        </Tooltip>
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            textAlign: "center",
            fontWeight: selected ? 600 : 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "90%",
          }}
        >
          {branch.name || "Branch"}
        </Typography>

        {footer(branch, selected)}
      </Box>
    );
  };
  
  
export const BranchSelector = ({
    selectedBranch,
    selectedBranches = [],
    onSelect,
    onSelectMultiple,
    selectFirstOnLoad,
    defaultSelectedBranchID,
    multiSelect = false,
    filter = (b: BranchType) => true,
    footer
  }: BranchSelectorProps) => {

  const [branches, setBranches] = useState<BranchType[]>();

  const {showMessage} = useSnackBar();
  const {config} = useContext(AuthContext);

  const handleBranchClick = (branch: BranchType) => {
    if (multiSelect && onSelectMultiple) {
      const isSelected = selectedBranches.some(b => b.id === branch.id);
      if (isSelected) {
        onSelectMultiple(selectedBranches.filter(b => b.id !== branch.id));
      } else {
        onSelectMultiple([...selectedBranches, branch]);
      }
    } else if (onSelect) {
      onSelect(branch);
    }
  };

  const isBranchSelected = (branch: BranchType): boolean => {
    if (multiSelect) {
      return selectedBranches.some(b => b.id === branch.id);
    }
    return selectedBranch?.id === branch.id;
  };

  useEffect(()=>{
    if (branches) {
      if (defaultSelectedBranchID && branches.length > 0) {
        const defaultBranch = branches.find(
          (b) => String(b.id) === String(defaultSelectedBranchID)
        ) ?? branches[0];
        if (defaultBranch && onSelect) {
          onSelect(defaultBranch);
          return;
        }
      }

      if (selectFirstOnLoad) {
        if (multiSelect && onSelectMultiple) {
          onSelectMultiple([branches[0]]);
        } else if (onSelect) {
          onSelect(branches[0]);
        }
      }

    }
  }, [branches])

    // Fetch all branches
    useEffect(() => {
      const fetchBranches = async () => {
        try {
          const response = await axios.get<{ results: BranchType[] }>(
            `${API_ENDPOINT}/api/v1/branches/?page_size=1000`,
            config
          );
          const fetchedBranches = response.data.results;
          setBranches(fetchedBranches.filter(filter));
      } catch {
          showMessage("Failed to fetch branches", "error");
        }
      };
      fetchBranches();
    }, [defaultSelectedBranchID, selectFirstOnLoad, selectedBranch?.id, showMessage]);

    return (
      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          margin: "auto",
          py: 2,
          px: 1,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 4,
          },
        }}
      >
        {branches ? branches.map((branch) => (
          <BranchCard
            key={branch.id}
            branch={branch}
            selected={isBranchSelected(branch)}
            onClick={handleBranchClick}
            footer={footer}
          />
        )):<Box width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"} gap={1}><CircularProgress size={20} /><Typography>Loading branches..</Typography></Box>}
      </Box>
    );
  };